import type {
  BsdGraphql,
  CancelCollectInputGraphql,
  CollectGraphql,
  CollectGraphqlFilters,
  CollectsCollection,
  CollectStatus,
  CreateCollectGraphql,
  Maybe,
  Mutation,
  PartialUserGraphql,
  RecyclerGraphql,
  SignAllCollectBsDsInput,
  SignBsdInput,
  UpdateCollectInformationsGraphql,
} from '~/types/graphql-backend-types/gql-types'
import { useGqlMikro } from '~/composables/useGqlMikro'
import {
  CANCEL_COLLECT,
  CREATE_COLLECT_MUTATION,
  SIGN_ALL_BSDS,
  SIGN_BSD,
  UPDATE_COLLECT_QUERY,
} from '~/queries/collects'
import { AscDescGraphql } from '~/types/graphql-backend-types/gql-types'

export interface MaterialContainerChoice {
  materialId: string
  containers: {
    id: string
    containerId: string
    documents: {
      previewUrls: string[]
      files: File[]
    }
  }[][]
}

/* This ref is used when user is editing the collect summary in the collect creation
  It is used to avoid the user to have to fill the same informations again
*/
export const isEditiingCollectSumamary = ref(false)

export const useCollectsStore = defineStore('collects', () => {
  const { mutate } = useGqlMikro()
  const { collect } = storeToRefs(useCollectStore())
  const { user } = storeToRefs(useAuthStore())
  const { uploadDocument } = useDocumentStore()

  const collects = ref<CollectsCollection>({} as CollectsCollection)
  const referents = ref<PartialUserGraphql[]>([])
  const materialContainerChoiceBeforeCreation = ref<MaterialContainerChoice[]>([])
  const selectedRecyclerWhenCreatingCollect = ref<Partial<RecyclerGraphql>>()

  const preCollectCreation: CreateCollectGraphql = reactive({
    siteId: '',
    notes: '',
    startsAt: '',
    endsAt: '',
    containers: [],
    referentUserId: '',
    clientId: '',
  })

  const page = ref<number>(1)
  const collectsLoading = ref<boolean>(true)
  const offset = computed(() => (page.value - 1) * 10)

  const referentName = ref<string>('')

  const filters: CollectGraphqlFilters = reactive({
    status: [] as CollectStatus[],
    orderBy: {
      field: 'startsAt',
      order: AscDescGraphql.Desc,
    },
    companyIds: [],
    search: '',
    organizationIds: [],
  })

  const isEmptyFilters = computed(() => {
    return (
      filters.status!.length === 0
      && filters.companyIds!.length === 0
      && filters.search === ''
    )
  })

  const collectsCount = computed(() => collects.value.count)

  const getCollects: ComputedRef<CollectGraphql[] | []> = computed(() => {
    if (collects?.value?.collection) {
      return collects.value.collection as CollectGraphql[]
    }
    return []
  }) as ComputedRef<CollectGraphql[] | []>

  const getCollectsAsOptions = computed(() =>
    collects.value?.collection?.map(item => ({
      label: item?.__typename,
      value: item?.id,
    })),
  )

  /**
   * Format the container data for the collect creation mutation
   * @param materials - The materials to format
   * @returns The formatted container data
   */
  function formatContainerData(materials: MaterialContainerChoice[]): { materialId: string, containerId: string, files: File[], uploadedFilePath: string[] }[] {
    return materials.map(materials =>
      materials.containers.flat().map(container => ({
        materialId: materials.materialId,
        containerId: container.containerId,
        files: container.documents.files,
        uploadedFilePath: [],
      })),
    ).flat()
  }

  /**
   * Create a collect
   * First upload the collected container documents
   * Then create the collect with the uploaded file paths
   * @returns The collect data
   */
  async function createCollect(): Promise<{ errors?: readonly any[], data?: CollectGraphql }> {
    // Upload collected container documents
    const collectContainers = formatContainerData(materialContainerChoiceBeforeCreation.value)
    for (const container of collectContainers) {
      for (const file of container.files) {
        const filePath = await uploadDocument({ file })
        container.uploadedFilePath.push(filePath)
      }
    }

    // Create collect
    const { data, errors } = await mutate({
      mutation: CREATE_COLLECT_MUTATION,
      variables: {
        input: {
          ...preCollectCreation,
          containers: collectContainers.map(container => ({
            ...container,
            // Prepare file payload
            uploadedFilePath: undefined,
            files: container.uploadedFilePath.map(filePath => ({ uploadPath: filePath })),
          })),
          clientId: user.value.client.id,
        },
      },
    })

    if (errors && errors.length > 0) {
      throw errors
    }
    resetPrecollectCreation()

    return { data: data?.createCollect }
  }

  async function updateCollectInformations(payload: UpdateCollectInformationsGraphql): Promise<Mutation['updateCollectInformations']> {
    const { data, errors } = await mutate({
      mutation: UPDATE_COLLECT_QUERY,
      variables: { input: payload },
    })

    if (errors && errors.length > 0) {
      throw errors
    }

    collect.value.status = data!.updateCollectInformations.status
    collect.value.startsAt = data!.updateCollectInformations.startsAt
    collect.value.transportPrice = data!.updateCollectInformations.transportPrice
    collect.value.licensePlate = data!.updateCollectInformations.licensePlate

    return data!.updateCollectInformations
  }

  async function updateCollectStatus(payload: { collectId: string, status: CollectStatus }): Promise<Mutation['updateCollectInformations']> {
    const { data, errors } = await mutate({
      mutation: UPDATE_COLLECT_QUERY,
      variables: { input: payload },
    })

    if (errors && errors.length > 0) {
      throw errors
    }

    collect.value.status = data!.updateCollectInformations.status

    findAndUpdateCollectStatus(payload.collectId, data!.updateCollectInformations.status)
    return data!.updateCollectInformations
  }

  function findAndUpdateCollectStatus(collectId: string, status: CollectStatus) {
    const coco = collects.value?.collection?.find(item => item?.id.toString() === collectId)
    if (!coco)
      return
    coco.status = status
  }

  function resetPrecollectCreation() {
    preCollectCreation.clientId = user.value.client.id
    preCollectCreation.siteId = ''
    preCollectCreation.referentUserId = ''
    preCollectCreation.containers = []
    materialContainerChoiceBeforeCreation.value = []
    preCollectCreation.notes = ''
    preCollectCreation.startsAt = ''
    preCollectCreation.endsAt = ''
    referentName.value = ''
  }

  function getCollectById(id: string): Maybe<CollectGraphql> | undefined {
    return collects.value.collection.find(item => item?.id.toString() === id.toString())
  }

  function updateBSD(payload: { collectId: string, BSD: BsdGraphql }) {
    const { BSD, collectId } = payload
    const collect = collects.value.collection.find(item => item?.id.toString() === collectId.toString())
    if (collect)
      collect.BSDs.collection[0] = BSD
  }

  async function cancelCollect(id: string): Promise<Mutation['cancelCollect']> {
    const { data, errors } = await mutate({
      mutation: CANCEL_COLLECT,
      variables: { input: { collectId: id } as CancelCollectInputGraphql },
    })

    if (errors && errors.length > 0) {
      throw errors
    }
    return data!.cancelCollect
  }

  async function signBSD(collectId: string, collectedContainerIds: string) {
    const input: SignBsdInput = {
      collectedContainerIds,
      collectId,
      signatureCode: 1,
    }

    const { data, errors } = await mutate({
      mutation: SIGN_BSD,
      variables: { input },
    })

    if (errors && errors.length > 0) {
      throw errors
    }

    return { data, errors }
  }

  async function signAllBSDs(collectId: string) {
    const input: SignAllCollectBsDsInput = {
      collectId,
      signatureCode: 1,
    }

    const { data, errors } = await mutate({
      mutation: SIGN_ALL_BSDS,
      variables: { input },
    })

    if (errors && errors.length > 0) {
      throw errors
    }

    return { data, errors }
  }

  function resetFilters() {
    filters.status = []
    filters.orderBy = { field: 'startsAt', order: AscDescGraphql.Asc }
    filters.companyIds = []
    filters.organizationIds = []
    filters.search = ''
  }

  return {
    filters,
    page,
    offset,
    collects,
    referents,
    isEmptyFilters,
    preCollectCreation,
    materialContainerChoiceBeforeCreation,
    collectsLoading,
    collectsCount,
    getCollects,
    referentName,
    getCollectsAsOptions,
    selectedRecyclerWhenCreatingCollect,
    createCollect,
    updateCollectInformations,
    updateCollectStatus,
    resetFilters,
    getCollectById,
    updateBSD,
    cancelCollect,
    signBSD,
    signAllBSDs,
    formatContainerData,
    resetPrecollectCreation,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCollectsStore, import.meta.hot))
}
